import React, { useState } from 'react'
import { toast } from 'react-toastify'
import { LoadingButton } from '@mui/lab'
import {
  Autocomplete,
  Box,
  CircularProgress,
  DialogActions,
  Divider,
  Grid,
  InputLabel,
  TextField
} from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import SubscriptionsAPI from 'api/faber/SubscriptionsAPI'
import { RoundedButton } from 'common/Button/Button'
import TextColorFontBody from 'common/Typography/TextColorFontBody'
import { useFormik } from 'formik'
import OrbitSession from 'hooks/useCookies'
import { SubscriptionsTableRows } from 'types/api/faber/subscription.type'
import * as yup from 'yup'

import { CustomerDetailsTextField } from './CustomerDetailsTextField'

const headerMap = {
  nextPlan: 'Next Cycle Plan'
}

function UserPlanForm({
  customerRow,
  onSuccessfulFormSubmission,
  nextPlan,
  handleDialogClose
}: UserPlanProps) {
  const faberToken = OrbitSession.get('faberToken')

  const [dialogSequence, setDialogSequence] = useState(0)
  const [initialValues, setInitialValues] = useState({
    nextPlan: nextPlan,
    remarks: ''
  })
  const validationSchema = yup.object({
    remarks: yup.string().required('Remarks are required')
  })

  const formik = useFormik({
    initialValues: initialValues,
    onSubmit: (values) => {
      updateCustomerSubscriptionPlan({
        customerID: customerRow.userID,
        newBundleID: values.nextPlan === 'Lite Pac' ? '101' : '105',
        faberToken: faberToken
      })
    },
    validationSchema
  })

  const {
    mutate: updateCustomerSubscriptionPlan,
    isLoading: isUpdatingReqLoading
  } = useMutation(SubscriptionsAPI.updateCustomerSubscriptionPlan, {
    onSuccess: () => {
      onSuccessfulFormSubmission()
      toast.success('Customer subscription is updated')
      setInitialValues({
        nextPlan: formik.values.nextPlan,
        remarks: ''
      })
    },
    onError: () => {
      toast.error('Error in updating customer subscription plan')
    },
    onSettled: () => {
      handleDialogClose()
    }
  })

  return (
    <>
      {dialogSequence === 0 && (
        <Box sx={{ px: 4 }}>
          {/* TO DO: To show when available */}
          {/* <Grid item xs={12}>
            <TextColorFontBody sx={{ fontSize: '16px', fontWeight: 400 }}>
              {customerRow.planName}
            </TextColorFontBody>
            <Divider sx={{ width: '100%' }} />
            <InputLabel sx={{ fontSize: '12px' }}>Current Plan Type</InputLabel>
          </Grid> */}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Autocomplete
              id='Next Cycle Plan Type'
              options={[
                'Lite Pac'
                //  'Pro Pac ($25)' //COMMENTED OUT UNTIL UPGRADE NOW IS PUSHED ON CH DASHBOARD
              ]}
              value={
                formik.values.nextPlan === 'Pro Plan'
                  ? 'Pro Pac ($25)'
                  : formik.values.nextPlan
              }
              disableClearable
              disabled={
                customerRow.planStatus === 'CANCELLED' ||
                customerRow.planStatus === 'SUSPENDED' ||
                customerRow.planStatus === 'UNSUBSCRIBED'
              }
              renderInput={(params) => (
                <TextField {...params} variant='standard' />
              )}
              onChange={(e, v) => {
                formik.setFieldValue('nextPlan', v)
              }}
            />
            <InputLabel sx={{ fontSize: '12px' }}>
              Next Cycle Plan Type
            </InputLabel>
          </Grid>
          <Box sx={{ my: 1 }}>
            <CustomerDetailsTextField
              variant='standard'
              id='remarks'
              multiline
              value={formik.values.remarks}
              onChange={(e) => {
                formik.handleBlur(e)
                formik.handleChange(e)
              }}
              sx={{ minWidth: '530px', resize: 'horizontal' }}
              InputProps={{
                sx: {
                  color: '#333',
                  fontSize: '14px'
                }
              }}
              error={formik.touched.remarks && Boolean(formik.errors.remarks)}
              helperText={formik.touched.remarks && formik.errors.remarks}
            />
            {!(formik.touched.remarks && Boolean(formik.errors.remarks)) ? (
              <InputLabel sx={{ fontSize: '12px' }}>Remarks</InputLabel>
            ) : null}
          </Box>
          <DialogActions>
            <RoundedButton
              variant='outlined'
              onClick={() => handleDialogClose()}
            >
              Cancel
            </RoundedButton>
            <RoundedButton
              disabled={!formik.values.remarks || !formik.isValid}
              variant='contained'
              onClick={() => setDialogSequence(1)}
              sx={{
                ':disabled': {
                  border: 'none'
                }
              }}
            >
              Confirm
            </RoundedButton>
          </DialogActions>
        </Box>
      )}
      {dialogSequence === 1 && (
        <>
          <form onSubmit={formik.handleSubmit}>
            <Grid
              container
              sx={{
                mt: 2,
                height: '5vh',
                padding: '4px 0',
                display: 'flex',
                alignItems: 'center',
                backgroundColor: '#f6f6f6',
                px: 2
              }}
            >
              <Grid item xs={6}>
                <TextColorFontBody
                  sx={{
                    fontWeight: 'bolder',
                    fontSize: '14px',
                    textAlign: 'left',
                    ml: 3
                  }}
                >
                  Current
                </TextColorFontBody>
              </Grid>
              <Grid item xs={6}>
                <TextColorFontBody
                  sx={{
                    fontWeight: 'bolder',
                    fontSize: '14px',
                    textAlign: 'left'
                  }}
                >
                  Changes made
                </TextColorFontBody>
              </Grid>
            </Grid>
            <Box sx={{ px: 4 }}>
              <Grid container px={1} mt={2} mb={1}>
                {Object.keys(headerMap).map((fieldName, index) => {
                  return (
                    <React.Fragment key={fieldName}>
                      <Grid
                        item
                        xs={6}
                        sx={{
                          textAlign: 'left',
                          fontSize: '12px',
                          wordBreak: 'break-all'
                        }}
                      >
                        {formik.initialValues[fieldName]}
                      </Grid>
                      <Grid
                        item
                        xs={6}
                        sx={{
                          textAlign: 'left',
                          fontSize: '12px',
                          wordBreak: 'break-all'
                        }}
                      >
                        {formik.values[fieldName]}
                      </Grid>
                      {index !== Object.keys(headerMap).length - 1 && (
                        <Divider sx={{ margin: '8px auto', width: '100%' }} />
                      )}
                    </React.Fragment>
                  )
                })}
                <Box sx={{ width: '100%', maxWidth: '500px', mt: 3 }}>
                  <InputLabel sx={{ fontSize: '12px' }}>Remarks</InputLabel>
                  <CustomerDetailsTextField
                    variant='standard'
                    id='remarks'
                    multiline
                    value={formik.values.remarks}
                    disabled
                    sx={{ minWidth: '520px', resize: 'horizontal' }}
                    InputProps={{
                      sx: {
                        color: '#333',
                        fontSize: '14px'
                      }
                    }}
                  />
                </Box>
              </Grid>
              <DialogActions>
                <RoundedButton
                  variant='outlined'
                  onClick={() => setDialogSequence(0)}
                >
                  Back
                </RoundedButton>
                <LoadingButton
                  type='submit'
                  variant='filled'
                  loadingIndicator={
                    <CircularProgress color='inherit' size={16} />
                  }
                  loading={isUpdatingReqLoading}
                >
                  Confirm
                </LoadingButton>
              </DialogActions>
            </Box>
          </form>
        </>
      )}
    </>
  )
}

export default UserPlanForm

interface UserPlanProps {
  customerRow: SubscriptionsTableRows
  onSuccessfulFormSubmission: () => void
  nextPlan: string | undefined
  handleDialogClose: () => void
}
